import React, { useState } from 'react';
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Tag,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { PiCopyBold, PiEnvelopeSimpleBold, PiLinkSimpleHorizontalDuotone } from 'react-icons/pi';
import { MdOutlineSms } from 'react-icons/md';
import FormatNumber from '../../../components/FormatNumber';
import { Required } from '../../../components/Required';
import { CreatePaymentLinkResponseData } from '../models/create-payment-link';
import { getPaymentLinkStatusColor } from '../models/payment-link-view';

interface PaymentLinkCreatedViewProps {
  data: CreatePaymentLinkResponseData;
  branchName: string;
  onCreateAnother: () => void;
  // Estado real del link (viene del BFF cuando se reutiliza esta vista para "Compartir"
  // un link ya existente desde la Consulta). Si no se pasa (flujo de recién creado),
  // se asume Pendiente.
  status?: string;
  primaryActionLabel?: string;
}

type ShareChannel = 'email' | 'sms' | null;

const formatExpireDate = (expireDate: string): string =>
  new Date(expireDate).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

export const PaymentLinkCreatedView = ({
  data, branchName, onCreateAnother, status, primaryActionLabel = 'Crear Nuevo Link de Pago',
}: PaymentLinkCreatedViewProps) => {
  const [shareChannel, setShareChannel] = useState<ShareChannel>(null);
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const toast = useToast();

  const paymentLinkUrl = data.url;
  const statusLabel = status || 'Pendiente';
  const statusTagColors = getPaymentLinkStatusColor(statusLabel);

  const toggleChannel = (channel: ShareChannel) => {
    setShareChannel((current) => (current === channel ? null : channel));
    setContact('');
    setMessage('');
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(paymentLinkUrl);
    toast({
      status: 'success',
      position: 'bottom-right',
      title: 'Link copiado',
      description: 'El link de pago se copió al portapapeles.',
    });
  };

  const handleSend = () => {
    // No hay endpoint del BFF para envío de link por email/SMS todavía; queda preparado
    // el flujo de UI (canal + mensaje + contacto) para conectarlo cuando exista.
    toast({
      status: 'info',
      position: 'bottom-right',
      title: 'Próximamente',
      description: `El envío por ${shareChannel === 'sms' ? 'SMS' : 'correo'} estará disponible en una próxima actualización.`,
    });
  };

  return (
    <Box backgroundColor="white" borderRadius="16px" padding="xxl" maxWidth="40em" marginX="auto">
      <Text fontSize="xl" fontWeight="bold">¡Listo! ya puedes compartir el link</Text>
      <Text color="neutral.600" marginBottom="xxl">
        Envía tu link de pago y recibe de manera fácil y rápida tus pagos.
      </Text>

      <Center flexDirection="column" marginBottom="xl">
        <Text color="neutral.600">Total de pago</Text>
        <FormatNumber
          value={Number(data.amount)}
          style="currency"
          currency="MXN"
          locale="es-MX"
          textProps={{ fontSize: 'xxxl', fontWeight: 'bold' }}
        />
        {!!branchName &&
          <Text fontWeight="bold" textTransform="uppercase" marginTop="s">{branchName}</Text>
        }
      </Center>

      <Flex flexWrap="wrap" gap="xl" marginBottom="l">
        <HStack>
          <Text color="neutral.600">Estatus:</Text>
          <Tag background={statusTagColors.background} color={statusTagColors.color} borderRadius="full" paddingX="l">
            {statusLabel}
          </Tag>
        </HStack>
        <HStack>
          <Text color="neutral.600">Fecha de vencimiento:</Text>
          <Text>{data.expire_date ? formatExpireDate(data.expire_date) : 'Sin fecha de vencimiento'}</Text>
        </HStack>
      </Flex>

      <Text marginBottom="xl">
        <Text as="span" color="neutral.600">Producto o servicio: </Text>
        {data.concept}
      </Text>

      <Text fontWeight="medium" marginBottom="s">Link de pago:</Text>
      <Flex
        alignItems="center"
        borderWidth="1px"
        borderColor="neutral.300"
        borderRadius="md"
        padding="l"
        marginBottom="xl"
        cursor="pointer"
        onClick={handleCopyLink}
      >
        <PiCopyBold />
        <Text marginLeft="l" fontSize="s" isTruncated>{paymentLinkUrl}</Text>
      </Flex>

      <Text fontWeight="medium" marginBottom="l">Puedes compartir el link de las siguientes maneras:</Text>
      <Flex gap="l" marginBottom="l">
        <Button flex="1" variant="outline" leftIcon={<PiEnvelopeSimpleBold />} onClick={() => toggleChannel('email')}>
          Email
        </Button>
        <Button flex="1" variant="outline" leftIcon={<MdOutlineSms />} onClick={() => toggleChannel('sms')}>
          SMS
        </Button>
      </Flex>

      {!!shareChannel &&
        <Box marginBottom="xl">
          <Text color="secondary.600" marginBottom="l">
            {shareChannel === 'sms'
              ? 'Ingrese el número telefónico para compartir el link por SMS'
              : 'Ingrese el correo electrónico para compartir el link por Email'}
          </Text>
          <Flex gap="l" flexWrap="wrap" marginBottom="l">
            <FormControl flex="1" minWidth="12em">
              <FormLabel>Mensaje:</FormLabel>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Envío de link..." />
            </FormControl>
            <FormControl flex="1" minWidth="12em">
              <FormLabel>{shareChannel === 'sms' ? 'Teléfono:' : 'Correo:'}<Required /></FormLabel>
              <Input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                type={shareChannel === 'sms' ? 'tel' : 'email'}
              />
            </FormControl>
          </Flex>
          <Button colorScheme="button-primary" width="100%" onClick={handleSend} isDisabled={!contact}>
            Enviar
          </Button>
        </Box>
      }

      <Divider borderStyle="dashed" marginY="xl" />

      <Center flexDirection="column" gap="l">
        <PiLinkSimpleHorizontalDuotone fontSize="6rem" color="var(--chakra-colors-info-800)" />
        <Button colorScheme="button-primary" borderRadius="full" width="100%" onClick={onCreateAnother}>
          {primaryActionLabel}
        </Button>
      </Center>
    </Box>
  );
};
